import React, { useMemo, useState } from 'react';
import { DropDown, SegmentedControl } from 'src/components-dummy';
import { MerchandisingRuleTypes } from 'src/services';
import { AddItemCard } from '../AddItemCard';
import { buildConditionValues, needsConditionValueInput } from './helpers';
import {
  ACTION_OPTIONS,
  INTENSITY_OPTIONS,
  INTENSITY_WEIGHT_BY_LEVEL,
  PromoteIntensityLevel,
} from './constants';
import { ConditionFieldOption, NewRuleDraft } from './types';
import { useConditionField } from './useConditionField';
import { ConditionValueInput } from './ConditionValueInput';

const { RuleAction } = MerchandisingRuleTypes;

export interface NewRuleComposerProps {
  fieldOptions: ConditionFieldOption[];
  onApply: (draft: NewRuleDraft) => void;
  onCancel: () => void;
}

export const NewRuleComposer = ({
  fieldOptions,
  onApply,
  onCancel,
}: NewRuleComposerProps): JSX.Element => {
  const [name, setName] = useState('');
  const [action, setAction] = useState<MerchandisingRuleTypes.RuleAction>(RuleAction.Include);
  const [intensity, setIntensity] = useState<PromoteIntensityLevel>(INTENSITY_OPTIONS[0].value);
  const {
    field,
    subType,
    value,
    selectedField,
    category,
    subTypeOptions,
    setValue,
    setSubType,
    handleFieldChange,
  } = useConditionField(fieldOptions, { field: '', subType: '', value: '' });

  const isWeightedAction = action === RuleAction.Promote || action === RuleAction.Demote;

  const summary = useMemo(() => {
    if (!field || !subType)
      return 'Pick a data field and condition to see how this rule will apply.';
    const conditionLabel = subTypeOptions.find(option => option.value === subType)?.text ?? '';
    const valueText = needsConditionValueInput(subType) ? ` ${value || '…'}` : '';
    const actionText = ACTION_OPTIONS.find(option => option.value === action)?.text ?? '';
    const intensityText = isWeightedAction ? ` (${intensity.toLowerCase()})` : '';
    return `${actionText}${intensityText} items where ${field} ${conditionLabel}${valueText}.`;
  }, [field, subType, subTypeOptions, value, action, isWeightedAction, intensity]);

  const isApplyDisabled =
    !name.trim() ||
    !field ||
    !subType ||
    (needsConditionValueInput(subType as MerchandisingRuleTypes.MerchandisingRuleSubType) &&
      value.trim() === '');

  const handleApply = (): void => {
    if (!selectedField || !subType) return;

    onApply({
      name: name.trim(),
      action,
      weight: isWeightedAction ? INTENSITY_WEIGHT_BY_LEVEL[intensity] : 0,
      field,
      fieldType: selectedField.fieldType,
      subType,
      values: buildConditionValues(subType, category, value),
    });
  };

  return (
    <AddItemCard
      title='New rule'
      onApply={handleApply}
      onCancel={onCancel}
      isApplyDisabled={isApplyDisabled}
      summary={summary}
    >
      <input
        type='text'
        placeholder='Rule name'
        className='syte-merch-rules-editor-add-row-name'
        value={name}
        onChange={event => setName(event.target.value)}
      />
      <DropDown
        placeholder='Action'
        options={ACTION_OPTIONS}
        value={action}
        onChange={val => setAction(val as MerchandisingRuleTypes.RuleAction)}
        className='syte-merch-rules-editor-add-row-action'
      />
      {isWeightedAction && (
        <SegmentedControl<PromoteIntensityLevel>
          options={INTENSITY_OPTIONS}
          value={intensity}
          onChange={setIntensity}
        />
      )}
      <DropDown
        placeholder='Data field'
        options={fieldOptions}
        value={field || undefined}
        onChange={val => handleFieldChange(String(val))}
        className='syte-merch-rules-editor-add-row-field'
      />
      <DropDown
        placeholder='Condition'
        options={subTypeOptions}
        value={subType || undefined}
        disabled={!field}
        onChange={val => {
          setSubType(val as MerchandisingRuleTypes.MerchandisingRuleSubType);
          setValue('');
        }}
        className='syte-merch-rules-editor-add-row-condition'
      />
      <ConditionValueInput
        subType={subType}
        category={category}
        value={value}
        onChange={setValue}
        className='syte-merch-rules-editor-add-row-value'
      />
    </AddItemCard>
  );
};
